import { __rest } from '../../../_virtual/_tslib.js';
import React__default from 'react';
import classNames from 'classnames';
import { determineDataTestIdPrefix, getBaseProps, getAriaProps } from '../../../helpers/propHelpers.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatNumber, stripNonNumericCharacters } from '../../../helpers/formatters.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { Menu } from '../Menu/Menu.js';

var LocaleEnum;
(function (LocaleEnum) {
    LocaleEnum["USD"] = "en-US";
    LocaleEnum["CAD"] = "en-CA";
    LocaleEnum["GBP"] = "en-GB";
})(LocaleEnum || (LocaleEnum = {}));
const CurrencyInput = (_a) => {
    var _b, _c, _d, _e, _f, _g;
    var { textAlign = 'right' } = _a, props = __rest(_a, ["textAlign"]);
    const [currencyVal, setValue] = React__default.useState('');
    const [openMenu, setOpenMenu] = React__default.useState(false);
    const [rightIconProps, setRightIconProps] = React__default.useState({
        rightIcon: props === null || props === void 0 ? void 0 : props.rightIcon,
        iconRightTooltipProps: props === null || props === void 0 ? void 0 : props.iconRightTooltipProps,
    });
    const formatCurrency = (val) => {
        // don't validate empty input
        if (!val) {
            return;
        }
        let input_val = typeof val === 'number' ? val.toFixed(2) : val;
        // check for decimal
        if (input_val.indexOf('.') >= 0) {
            // get position of first decimal
            // this prevents multiple decimals from
            // being entered
            const decimal_pos = input_val.indexOf('.');
            // split number by decimal point
            let left_side = input_val.substring(0, decimal_pos);
            let right_side = input_val.substring(decimal_pos);
            // add commas to left side of number
            left_side = formatNumber(left_side);
            // validate right side
            right_side = formatNumber(right_side).replace(',', '');
            // make sure 2 numbers after decimal
            right_side += '00';
            // Limit decimal to only 2 digits
            right_side = right_side.substring(0, 2);
            // join number by .
            input_val = left_side + '.' + right_side;
        }
        else {
            // no decimal entered
            // add commas to number
            // remove all non-digits
            input_val = formatNumber(input_val);
            input_val += '.00';
        }
        return input_val;
    };
    React__default.useEffect(() => {
        if (formatCurrency(props.value) !== formatCurrency(currencyVal)) {
            if (!props.value) {
                setValue('');
            }
            else {
                setValue(formatCurrency(props === null || props === void 0 ? void 0 : props.value));
            }
        }
        showOverrideIconTooltip(props.value);
    }, [props.value, props.overrideCurrencyValue]);
    React__default.useEffect(() => {
        showOverrideIconTooltip(props === null || props === void 0 ? void 0 : props.value);
    }, [props === null || props === void 0 ? void 0 : props.rightIcon, props === null || props === void 0 ? void 0 : props.iconRightTooltipProps]);
    const getCurrencyIcon = () => {
        let currencyIcon;
        switch (props.currency) {
            case 'USD':
                currencyIcon = { icon: 'dollar-sign' };
                break;
            case 'CAD':
                currencyIcon = { icon: 'dollar-sign' };
                break;
            case 'GBP':
                currencyIcon = { icon: 'pound-sign' };
                break;
            default:
                currencyIcon = { icon: 'dollar-sign' };
        }
        return currencyIcon;
    };
    const handleKeyDown = (e) => {
        // prevent more than 1 decimal getting added
        const stripped = stripNonNumericCharacters(e.target.value);
        const decimalsCount = stripped.split('.').length - 1;
        if (e.key === '.' && decimalsCount > 0) {
            e.preventDefault();
        }
    };
    const showMenu = () => {
        setOpenMenu(true);
    };
    const closeMenu = () => {
        setOpenMenu(false);
    };
    const handleMenuClick = () => {
        closeMenu();
        setValue(formatCurrency(props === null || props === void 0 ? void 0 : props.overrideCurrencyValue));
    };
    const handleTabKeyEvent = (event) => {
        if (event.key === 'tab' || event.key === 9 || event.key === 'Tab') {
            closeMenu();
        }
    };
    const showOverride = (value) => {
        return (!!(props === null || props === void 0 ? void 0 : props.isOverrideEnabled) &&
            !!formatCurrency(props === null || props === void 0 ? void 0 : props.overrideCurrencyValue) &&
            formatCurrency(props === null || props === void 0 ? void 0 : props.overrideCurrencyValue) !== formatCurrency(value));
    };
    const showOverrideIconTooltip = (value) => {
        var _a, _b;
        const overrideIconProps = {
            rightIcon: props === null || props === void 0 ? void 0 : props.overrideIcon,
            iconRightTooltipProps: Object.assign(Object.assign({}, props === null || props === void 0 ? void 0 : props.iconOverrideTooltipProps), { customDescriptionTemplate: tooltipCustomDescriptionTemplate([
                    (_a = props === null || props === void 0 ? void 0 : props.iconOverrideTooltipProps) === null || _a === void 0 ? void 0 : _a.description,
                    (_b = props === null || props === void 0 ? void 0 : props.iconRightTooltipProps) === null || _b === void 0 ? void 0 : _b.description,
                ]) }),
        };
        const rightIconProps = {
            rightIcon: props === null || props === void 0 ? void 0 : props.rightIcon,
            iconRightTooltipProps: props === null || props === void 0 ? void 0 : props.iconRightTooltipProps,
        };
        showOverride(value) ? setRightIconProps(overrideIconProps) : setRightIconProps(rightIconProps);
    };
    const overrideMenuItems = [
        {
            label: `${props === null || props === void 0 ? void 0 : props.overrideMenuDescription}`,
            handleClick: () => {
                handleMenuClick();
                closeMenu();
            },
        },
    ];
    const tooltipCustomDescriptionTemplate = (descriptions) => {
        return (React__default.createElement("div", null, descriptions.map((desc) => (React__default.createElement("div", { "data-testid": `menu-item-${desc}`, key: `${dataTestId}-${desc}` }, desc)))));
    };
    const dataTestId = determineDataTestIdPrefix(props, 'currency-input');
    const currencyIcon = (props === null || props === void 0 ? void 0 : props.leftIcon) || getCurrencyIcon();
    const iconClassName = (currencyIcon === null || currencyIcon === void 0 ? void 0 : currencyIcon.className) || '';
    const baseProps = getBaseProps(props);
    getAriaProps(props);
    return (React__default.createElement(React__default.Fragment, null,
        props.label && (React__default.createElement("label", { htmlFor: "", className: "field__label", "data-testid": `${dataTestId}-label` },
            props.label,
            props.required && (React__default.createElement("span", { "data-testid": `${dataTestId}-required-label`, className: "field__required-indicator" }, "*")))),
        React__default.createElement("div", Object.assign({}, baseProps, { className: classNames({
                'currency-input': true,
                'currency-input--error': props.hasErrors,
                'currency-input--success': props.hasSuccess,
                'currency-input__tooltip': (props.leftIcon && props.iconLeftTooltipProps) ||
                    (rightIconProps.rightIcon && rightIconProps.iconRightTooltipProps),
            }, props === null || props === void 0 ? void 0 : props.className), "data-testid": `${dataTestId}` }),
            props.showCurrencyIcon ? (React__default.createElement(FontAwesomeIcon, { icon: currencyIcon === null || currencyIcon === void 0 ? void 0 : currencyIcon.icon, "data-testid": `${dataTestId}-icon`, className: `currency-input__left-icon ${iconClassName}` })) : props.leftIcon && props.iconLeftTooltipProps ? (React__default.createElement(Tooltip, { description: props.iconLeftTooltipProps.description, customDescriptionTemplate: props.iconLeftTooltipProps.customDescriptionTemplate, headline: props.iconLeftTooltipProps.headline, headlineIcon: props.iconLeftTooltipProps.headlineIcon, position: props.iconLeftTooltipProps.position, "data-testid": `tooltip-${dataTestId}` },
                React__default.createElement(FontAwesomeIcon, { icon: (_b = props === null || props === void 0 ? void 0 : props.leftIcon) === null || _b === void 0 ? void 0 : _b.icon, "data-testid": `fa-left-${dataTestId}-icon`, className: `currency-input__left-icon ${(_c = props.leftIcon) === null || _c === void 0 ? void 0 : _c.className}` }))) : (props.leftIcon && (React__default.createElement(FontAwesomeIcon, { icon: (_d = props === null || props === void 0 ? void 0 : props.leftIcon) === null || _d === void 0 ? void 0 : _d.icon, "data-testid": `${dataTestId}-icon`, className: `currency-input__left-icon ${(_e = props.leftIcon) === null || _e === void 0 ? void 0 : _e.className}` }))),
            React__default.createElement("input", { id: props === null || props === void 0 ? void 0 : props.id, name: props === null || props === void 0 ? void 0 : props.name, type: "text", className: classNames({
                    'currency-input__input': true,
                    'currency-input__input--error': props.hasErrors,
                    'currency-input__input--success': props.hasSuccess,
                    'currency-input__input--has-left-icon': props.showCurrencyIcon,
                    'currency-input__input--has-right-icon': rightIconProps.rightIcon,
                    [`currency-input__input--text-align-${textAlign}`]: true,
                }), value: currencyVal, maxLength: props === null || props === void 0 ? void 0 : props.maxLength, placeholder: props.placeholder, disabled: props.disabled, onBlur: (e) => {
                    var _a;
                    setValue(formatCurrency(e.target.value));
                    closeMenu();
                    showOverrideIconTooltip(currencyVal);
                    (_a = props === null || props === void 0 ? void 0 : props.handleBlur) === null || _a === void 0 ? void 0 : _a.call(props, e);
                }, onKeyDown: (e) => {
                    handleKeyDown(e);
                }, onChange: (e) => {
                    var _a;
                    setValue(e.target.value.replace(/[^\d,.-]/g, ''));
                    showOverrideIconTooltip(currencyVal);
                    (_a = props === null || props === void 0 ? void 0 : props.handleChange) === null || _a === void 0 ? void 0 : _a.call(props, e);
                }, onFocus: (e) => {
                    var _a;
                    if (showOverride(currencyVal))
                        showMenu();
                    (_a = props === null || props === void 0 ? void 0 : props.handleFocus) === null || _a === void 0 ? void 0 : _a.call(props, e);
                }, "data-testid": `${dataTestId}-input` }),
            rightIconProps.rightIcon && rightIconProps.iconRightTooltipProps ? (React__default.createElement(Tooltip, { description: rightIconProps.iconRightTooltipProps.description, customDescriptionTemplate: rightIconProps.iconRightTooltipProps.customDescriptionTemplate, headline: rightIconProps.iconRightTooltipProps.headline, headlineIcon: rightIconProps.iconRightTooltipProps.headlineIcon, position: rightIconProps.iconRightTooltipProps.position, "data-testid": `tooltip-${dataTestId}` },
                React__default.createElement(FontAwesomeIcon, { icon: (_f = rightIconProps === null || rightIconProps === void 0 ? void 0 : rightIconProps.rightIcon) === null || _f === void 0 ? void 0 : _f.icon, "data-testid": `${dataTestId}-icon`, className: `currency-input__right-icon ${rightIconProps === null || rightIconProps === void 0 ? void 0 : rightIconProps.rightIcon.className}` }))) : (rightIconProps.rightIcon && (React__default.createElement(FontAwesomeIcon, { icon: (_g = rightIconProps === null || rightIconProps === void 0 ? void 0 : rightIconProps.rightIcon) === null || _g === void 0 ? void 0 : _g.icon, "data-testid": `${dataTestId}-icon`, className: `currency-input__right-icon ${rightIconProps === null || rightIconProps === void 0 ? void 0 : rightIconProps.rightIcon.className}` })))),
        openMenu ? (React__default.createElement(Menu, { showMenu: openMenu, itemList: overrideMenuItems, onTabKeyPress: handleTabKeyEvent, position: 'bottom-start', hideButton: true, "data-testid": `currency-${dataTestId}` })) : (React__default.createElement(React__default.Fragment, null))));
};

export { CurrencyInput, LocaleEnum, CurrencyInput as default };
//# sourceMappingURL=CurrencyInput.js.map
